/** @format */

import React from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Policy } from '../services/getPolicies';

interface ExportPoliciesButtonProps {
  data: Partial<Policy>[];
  loading?: boolean;
}

const ExportPoliciesButton: React.FC<ExportPoliciesButtonProps> = ({ data, loading }) => {
  const flattenPolicyData = (policies: Partial<Policy>[]) => {
    const flattened: any[] = [];

    policies.forEach((policy) => {
      // Base policy data
      const baseData = {
        firstName: policy.firstName || '',
        lastName: policy.lastName || '',
        phoneNumber: policy.phoneNumber || '',
        campaign: policy.vendorName || '',
        office: policy.office?.officeName || '',
        FFMId: policy.FFMId || '',
        memberId: policy.memberId || '',
        agentName: `${policy.user?.firstName || ''} ${policy.user?.lastName || ''}`.trim(),
    
      };

      // Check if there are nested objects
      const hasMajorMed = policy.majorMed && Object.values(policy.majorMed).some((v) => v);
      const hasPrivateMed = policy.privateMed && Object.values(policy.privateMed).some((v) => v);
      const hasAncillaries = policy.ancillaries && Array.isArray(policy.ancillaries) && policy.ancillaries.length > 0;

      if (hasMajorMed) {
        const majorMedRow = {
          ...baseData,
          policyType: 'Major Med',
          carrier: policy.majorMed?.carrier || '',
          billingDate: policy.majorMed?.billingDate ? formatDate(policy.majorMed.billingDate) : '',
          premium: policy.majorMed?.premium || '',
          policyStatus: policy.majorMed?.status || '',
        };
        // Only add if not all blank (carrier, billingDate, premium)
        if (majorMedRow.carrier || majorMedRow.billingDate || majorMedRow.premium) {
          flattened.push(majorMedRow);
        }
      }

      if (hasPrivateMed) {
        const privateMedRow = {
          ...baseData,
          policyType: 'Private Med',
          carrier: policy.privateMed?.carrier || '',
          billingDate: policy.privateMed?.billingDate ? formatDate(policy.privateMed.billingDate) : '',
          premium: policy.privateMed?.premium || '',
          policyStatus: policy.privateMed?.status || '',
        };
        // Only add if not all blank (carrier, billingDate, premium)
        if (privateMedRow.carrier || privateMedRow.billingDate || privateMedRow.premium) {
          flattened.push(privateMedRow);
        }
      }

      if (hasAncillaries) {
        policy.ancillaries?.forEach((ancillary: any) => {
          if (ancillary && Object.values(ancillary).some((v) => v)) {
            const ancillaryRow = {
              ...baseData,
              policyType: 'Ancillary',
              carrier: ancillary.carrier || '',
              billingDate: ancillary.billingDate ? formatDate(ancillary.billingDate) : '',
              premium: ancillary.premium || '',
              policyStatus: ancillary.status || '',
            };
            // Only add if not all blank (carrier, billingDate, premium)
            if (ancillaryRow.carrier || ancillaryRow.billingDate || ancillaryRow.premium) {
              flattened.push(ancillaryRow);
            }
          }
        });
      }

      // Note: We no longer add base data without nested objects
    });

    return flattened;
  };

  const formatDate = (date: string) => {
    if (!date) return '';
    const parts = date.split('T')[0].split('-');
    if (parts.length !== 3) return '';
    const month = parts[1];
    const day = parts[2];
    const year = parts[0];
    return `${month}/${day}/${year}`;
  };

  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';

    const headers = [
      'First Name',
      'Last Name',
      'Phone Number',
      'Campaign',
      'Office',
      'FFMId',
      'MemberId',
      'Agent Name',
      'Policy Type',
      'Carrier',
      'Billing Date',
      'Premium',
      'Policy Status',
    ];

    const csvRows = [headers.join(',')];

    data.forEach((row) => {
      const values = [
        row.firstName,
        row.lastName,
        row.phoneNumber,
        row.campaign,
        row.office,
        row.FFMId,
        row.memberId,
        row.agentName,
        row.policyType,
        row.carrier,
        row.billingDate,
        row.premium,
        row.policyStatus,
      ];

      // Escape commas and quotes in CSV values
      const escapedValues = values.map((value) => {
        const stringValue = String(value || '');
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      });

      csvRows.push(escapedValues.join(','));
    });

    return csvRows.join('\n');
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    const flattenedData = flattenPolicyData(data);
    const csv = convertToCSV(flattenedData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `policies_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={handleExport}
      loading={loading}
      disabled={!data || data.length === 0}
    >
      Export CSV
    </Button>
  );
};

export default ExportPoliciesButton;
